import { isSafeInternalReturnPath } from '@gonfalon/navigator/isSafeInternalPath';

import { getIsValidRedirectPath } from '#utils/urlUtils';

function isSafeReturnUrl(returnUrl?: string): returnUrl is string {
  return Boolean(returnUrl && getIsValidRedirectPath(returnUrl) && isSafeInternalReturnPath(returnUrl));
}

export { isSafeReturnUrl };

function isChooseOrganizationRedirect(location: string) {
  return location.includes('/choose-organization/');
}

function toSameOriginPath(location: string) {
  try {
    const url = new URL(location, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch {
    return location;
  }
}

function getRedirectLocationFromResponse(res: Response) {
  if (res.redirected && res.url && isChooseOrganizationRedirect(res.url)) {
    return res.url;
  }

  if (res.status === 303 || res.status === 302) {
    const location = res.headers.get('Location');
    if (location) {
      return toSameOriginPath(location);
    }
  }

  if (res.redirected && res.url) {
    return res.url;
  }

  return undefined;
}

function appendReturnUrlToWelcomeSurvey(location: string, returnUrl?: string) {
  if (!isSafeReturnUrl(returnUrl) || !location.includes('/welcome-survey')) {
    return location;
  }

  try {
    const url = new URL(location, window.location.origin);
    if (!url.searchParams.has('returnUrl')) {
      url.searchParams.set('returnUrl', returnUrl);
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return location;
  }
}

export function getChooseOrganizationRedirectLocation(res: Response) {
  const location = getRedirectLocationFromResponse(res);
  if (location && isChooseOrganizationRedirect(location)) {
    return location;
  }

  return undefined;
}

export function isWelcomeSurveyLoginRedirect(res: Response) {
  const location = getRedirectLocationFromResponse(res);
  if (location?.includes('/welcome-survey')) {
    return true;
  }

  return Boolean(res.redirected && res.url?.includes('/welcome-survey'));
}

export function getPostLoginRedirectLocation(res: Response, returnUrl?: string) {
  const location = getRedirectLocationFromResponse(res);

  if (!location || isChooseOrganizationRedirect(location)) {
    return undefined;
  }

  return appendReturnUrlToWelcomeSurvey(location, returnUrl);
}

export function navigateToWelcomeSurvey(returnUrl?: string) {
  window.location.replace(appendReturnUrlToWelcomeSurvey('/welcome-survey', returnUrl));
}

/** Navigates to welcome-survey for session-login redirect responses; returns whether navigation started. */
export function completeWelcomeSurveyLoginRedirect(res: Response, returnUrl?: string) {
  if (!isWelcomeSurveyLoginRedirect(res)) {
    return false;
  }

  if (!assignPostLoginRedirect(res, returnUrl)) {
    navigateToWelcomeSurvey(returnUrl);
  }

  return true;
}

export function assignPostLoginRedirect(res: Response, returnUrl?: string) {
  const location = getPostLoginRedirectLocation(res, returnUrl);
  if (location) {
    window.location.replace(location);
    return true;
  }

  return false;
}
